/**
 * Striscia sopra il campo di scrittura quando la chat e' da ricontattare (scaduta)
 * o il cliente ha scritto dopo che il promemoria e' stato impostato.
 * "Fatto" chiude il Task commerciale (o il promemoria sulla chat), "Rimanda" riapre le scelte.
 */
import { useState } from 'react';
import { BellRing, Check, Clock } from 'lucide-react';
import { tip } from '@/components/messages/ChatTooltip';
import { recontactStatus, recontactStripText } from '@/lib/services/whatsapp/recontactRules';
import type { ChatLike } from '@/lib/services/whatsapp/chatListRules';
import { useChatRecontact } from './useChatRecontact';
import { RecontactPicker } from './RecontactPicker';

interface Props {
  chat: ChatLike & { name?: string };
  organizationId: string | null | undefined;
}

export function RecontactStrip({ chat, organizationId }: Props) {
  const { reminder, complete } = useChatRecontact(chat.id, organizationId);
  const [picker, setPicker] = useState<{ x: number; y: number } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!reminder) return null;
  const status = recontactStatus(reminder, chat);
  if (!status.due && !status.replied) return null;

  const done = async () => {
    setBusy(true);
    await complete();
    setBusy(false);
  };

  const btn = 'flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border transition-colors disabled:opacity-50';
  return (
    <div className="flex items-center gap-2 px-4 py-2 border-t border-[#F5D9B5] dark:border-amber-900/40 bg-[#FBF3DB] dark:bg-amber-900/15">
      <BellRing className="w-4 h-4 flex-shrink-0 text-[#D9730D]" />
      <span className="flex-1 min-w-0 truncate text-sm text-[#37352F] dark:text-white" {...tip(recontactStripText(reminder, status))}>
        {recontactStripText(reminder, status)}
        {status.due && status.replied && <span className="ml-1.5 text-xs text-[#0F7B6C]">ha risposto</span>}
      </span>
      <button type="button" disabled={busy} onClick={() => void done()}
        {...tip(reminder.taskId ? 'Segna il Task commerciale come completato' : 'Togli il promemoria da questa chat')}
        className={`${btn} border-[#0F7B6C]/30 text-[#0F7B6C] bg-white dark:bg-transparent hover:bg-[#0F7B6C]/10`}>
        <Check className="w-3.5 h-3.5" />Fatto
      </button>
      <button type="button" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setPicker({ x: r.right - 260, y: r.top - 330 }); }}
        {...tip('Sposta il promemoria a un altro giorno')}
        className={`${btn} border-[rgba(55,53,47,0.16)] dark:border-white/15 text-[#37352F] dark:text-white bg-white dark:bg-transparent hover:bg-[rgba(55,53,47,0.06)]`}>
        <Clock className="w-3.5 h-3.5" />Rimanda
      </button>
      {picker && (
        <RecontactPicker x={picker.x} y={picker.y} chatKey={chat.id} chatName={chat.name || ''} organizationId={organizationId} onClose={() => setPicker(null)} />
      )}
    </div>
  );
}
